"use client";

import { useEffect } from "react";

/**
 * Carga diferida de scripts/estilos de terceros (AdSense, analítica, GTM,
 * widgets de chat, etc.) configurados por el admin en /admin/etiquetas.
 *
 * En lugar de ejecutarse en el primer render —lo que penaliza LCP/TBT y
 * PageSpeed—, estas etiquetas se inyectan en el DOM SOLO cuando el usuario
 * interactúa por primera vez (scroll, movimiento de ratón, toque, tecla o
 * clic). Hay además un timeout de respaldo para que AdSense termine cargando
 * aunque el usuario nunca interactúe (preserva ingresos publicitarios).
 *
 * Los snippets de verificación de sitio / SEO (meta, link no-stylesheet,
 * base, style, noscript) se siguen renderizando en el servidor desde el
 * layout; aquí solo diferimos lo "pesado" de terceros.
 */

const INTERACTION_EVENTS = [
  "scroll",
  "mousemove",
  "touchstart",
  "keydown",
  "pointerdown",
  "click",
] as const;

// Respaldo: si nadie interactúa, carga igualmente pasado este tiempo.
const FALLBACK_MS = 5000;

type DeferredTagsProps = {
  /** HTML crudo de etiquetas <head> del admin. */
  head?: string | null;
  /** HTML crudo de etiquetas al inicio del <body> del admin. */
  bodyStart?: string | null;
  /** HTML crudo de etiquetas al final del <body> del admin. */
  bodyEnd?: string | null;
  /** HTML crudo de etiquetas de pie del admin. */
  footer?: string | null;
};

/** Recrea un <script> para que el navegador lo ejecute (innerHTML no ejecuta). */
function reviveScript(source: HTMLScriptElement): HTMLScriptElement {
  const script = document.createElement("script");
  for (const attr of Array.from(source.attributes)) {
    script.setAttribute(attr.name, attr.value);
  }
  if (source.textContent) script.textContent = source.textContent;
  return script;
}

/** Inyecta el HTML crudo en el documento, ejecutando scripts y estilos. */
function injectHtml(html: string | null | undefined, target: HTMLElement) {
  if (!html || !html.trim()) return;
  const template = document.createElement("template");
  template.innerHTML = html;
  for (const node of Array.from(template.content.childNodes)) {
    if (node.nodeType !== Node.ELEMENT_NODE) {
      target.appendChild(node);
      continue;
    }
    const el = node as HTMLElement;
    if (el.tagName === "SCRIPT") {
      target.appendChild(reviveScript(el as HTMLScriptElement));
    } else {
      // Los <script> anidados dentro de un contenedor tampoco se ejecutan
      // al venir de innerHTML; los recreamos uno por uno.
      const nested = el.querySelectorAll("script");
      target.appendChild(el);
      nested.forEach((old) => {
        old.replaceWith(reviveScript(old));
      });
    }
  }
}

export function DeferredTags({ head, bodyStart, bodyEnd, footer }: DeferredTagsProps) {
  useEffect(() => {
    if (!head && !bodyStart && !bodyEnd && !footer) return;

    let loaded = false;
    const load = () => {
      if (loaded) return;
      loaded = true;
      cleanup();
      injectHtml(head, document.head);
      // bodyStart se inyecta al principio del body para respetar el orden.
      if (bodyStart && bodyStart.trim()) {
        const holder = document.createElement("div");
        holder.style.display = "contents";
        document.body.insertBefore(holder, document.body.firstChild);
        injectHtml(bodyStart, holder);
      }
      injectHtml(bodyEnd, document.body);
      injectHtml(footer, document.body);
    };

    const cleanup = () => {
      for (const evt of INTERACTION_EVENTS) {
        window.removeEventListener(evt, load);
      }
      clearTimeout(timer);
    };

    for (const evt of INTERACTION_EVENTS) {
      window.addEventListener(evt, load, { once: true, passive: true });
    }
    const timer = window.setTimeout(load, FALLBACK_MS);

    return cleanup;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return null;
}
